<template>
  <div class="popup-form record-picker-popup">
    <div class="content-form-popup record-picker-content">
      <div class="header-text">
        <modal-close-button aria-label="Zavrieť výdavky" @click="closeDialog" />
        <div class="_900 big">{{ dialogTitle }}</div>
      </div>

      <div v-if="clientInfo" class="header-block inline">
        <div class="bold">{{ clientInfo.type }}</div>
        <img :src="getIcon(clientInfo.type)" loading="lazy" alt="" class="image-10" />
      </div>

      <record-picker-tabs
        v-if="showTabs"
        v-model="activeSection"
        :registered-count="debtsGetter.length"
        aria-label="Sekcie výdavkov"
      />

      <div class="w-form">
        <div id="email-form" class="form-2">
          <section v-if="activeSection === 'registered'" class="picker-section">
            <div class="section-intro">
              <div class="section-intro-title">Evidované výdavky</div>
              <div v-if="type === 'classic'" class="section-intro-copy">
                Pri každom úvere nastavte, či sa ponechá, splatí pred čerpaním alebo refinancuje.
              </div>
              <div v-else-if="type === 'refinancovane'" class="section-intro-copy">
                Vyberte výdavok, ktorý sa má splatiť novou hypotékou.
              </div>
            </div>

            <div
              v-for="debt in debtsGetter"
              :key="debt.id"
              class="record-card"
              :class="debtPlanClass(debt)"
            >
              <div class="record-main">
                <div class="record-name">
                  {{ translateDebtType(debt.typ) }}
                  <strong v-if="debt.banka">{{ translateBank(debt.banka) }}</strong>
                  <span v-if="debt.cislo_uveru" class="record-type">č. {{ debt.cislo_uveru }}</span>
                </div>
                <v-progress-circular
                  v-if="debt.loading && updatingDebtId !== debt.id"
                  class="loading-spinner extrasmall"
                  indeterminate
                ></v-progress-circular>
                <div v-else class="record-amount">{{ debtAmount(debt) | dotTheAmount }} EUR</div>
              </div>

              <div v-if="debt.error" class="record-warning">
                Výdavok treba dokončiť, aby sa dal správne vypočítať.
              </div>

              <div class="record-actions" :class="{single: type === 'pickerOnly'}">
                <div v-if="type !== 'pickerOnly'" class="record-management">
                  <button
                    type="button"
                    class="record-edit"
                    :disabled="updatingDebtId === debt.id"
                    @click="edit(debt)"
                  >
                    <app-icon name="edit" :size="17" />
                    {{ debt.error ? "Dokončiť" : "Upraviť" }}
                  </button>
                  <button
                    v-if="debt.can_delete !== false"
                    type="button"
                    class="record-delete"
                    :disabled="updatingDebtId === debt.id"
                    @click="removeDebt(debt)"
                  >
                    <app-icon name="trash" :size="17" />
                    Zmazať
                  </button>
                  <div v-else class="record-delete-locked">
                    {{ debt.delete_block_reason }}
                  </div>
                </div>

                <debt-plan-selector
                  v-if="type === 'classic'"
                  :value="debtPlan(debt)"
                  :busy="updatingDebtId === debt.id"
                  @change="changeDebtPlan(debt, $event)"
                />

                <button
                  v-else-if="type === 'refinancovane'"
                  type="button"
                  class="record-select"
                  @click="pick(debt)"
                >
                  {{ debtPlan(debt) === "refinance" ? "Refinancuje sa" : "Refinancovať" }}
                </button>

                <button
                  v-else
                  type="button"
                  class="record-select"
                  @click="pick(debt)"
                >
                  Vybrať
                </button>
              </div>
            </div>

            <div v-if="debtsGetter.length === 0" class="empty-records">
              <div class="empty-records-icon"><app-icon name="plus" :size="22" /></div>
              <div class="empty-records-title">Zatiaľ tu nie je žiadny výdavok</div>
              <div class="empty-records-copy">Pridajte prvý výdavok klienta a aplikácia ho zahrnie do výpočtov.</div>
              <button v-if="showTabs" type="button" class="empty-records-action" @click="activeSection = 'new'">
                Pridať výdavok
              </button>
            </div>
          </section>

          <section v-else class="picker-section new-record-section">
            <div class="splitter">Vyberte typ <strong>výdavku</strong></div>
            <button type="button" class="option-block record-option" @click="debtTypePicker('hypoteka')">
              <span class="bold">hypotéka</span>
              <app-icon name="plus" :size="19" />
            </button>
            <button type="button" class="option-block record-option" @click="debtTypePicker('povolene_precerpanie')">
              <span class="bold">povolené prečerpanie</span>
              <app-icon name="plus" :size="19" />
            </button>
            <button type="button" class="option-block record-option" @click="debtTypePicker('leasing')">
              <span class="bold">leasing</span>
              <app-icon name="plus" :size="19" />
            </button>
            <button type="button" class="option-block record-option" @click="debtTypePicker('kreditna_karta')">
              <span class="bold">kreditná karta</span>
              <app-icon name="plus" :size="19" />
            </button>
            <button type="button" class="option-block record-option" @click="debtTypePicker('spotrebny_uver')">
              <span class="bold">spotrebný úver</span>
              <app-icon name="plus" :size="19" />
            </button>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import translateFromDBValue from "../../../../../mixins/translateFromDBValue";
import clientFilters from "../../../../../mixins/clientFilters";
import AppIcon from "@/components/shared/AppIcon.vue";
import DebtPlanSelector from "@/components/shared/DebtPlanSelector.vue";
import ModalCloseButton from "@/components/shared/ModalCloseButton.vue";
import RecordPickerTabs from "@/components/shared/RecordPickerTabs.vue";

export default {
  name: "ClientDebtPicker",
  components: {AppIcon, DebtPlanSelector, ModalCloseButton, RecordPickerTabs},
  props: {
    isClicked: Boolean,
    rules: [Array, null],
    type: [String, null],
  },
  mixins: [translateFromDBValue, clientFilters],
  data: () => ({
    activeSection: "registered",
    debts: null,
    clientInfo: null,
    updatingDebtId: null,
  }),
  computed: {
    dialogTitle () {
      if (this.type === "pickerOnly") return "Vyberte výdavok";
      if (this.type === "refinancovane") return "Refinancované výdavky";
      return "Výdavky";
    },
    showTabs () {
      return this.type !== "pickerOnly";
    },
    activeMortgageId () {
      const mortgageId = parseInt(this.$route.params.hypoteka);
      return Number.isNaN(mortgageId) ? null : mortgageId;
    },
    debtsGetter () {
      if (["refinancovane", "pickerOnly"].includes(this.type)) {
        return this.$store.getters["mortgageDetailState/getAllDebts"]
          .filter((debt) => debt.nahradeny_dlhom == null);
      }

      if (!this.clientInfo) return [];
      return this.$store.getters["mortgageDetailState/getClientDebts"](this.clientInfo.type_code, false)
        .filter((debt) => debt.nahradeny_dlhom == null);
    },
  },
  created () {
    this.clientInfo = this.$store.getters["popupManagerState/getClientInfo"];
    this.activeSection = this.showTabs && this.debtsGetter.length === 0 ? "new" : "registered";
  },
  methods: {
    getIcon (type) {
      if (type === "Žiadateľ") return require("@/images/avatar_applicant.png");
      if (type === "Spolužiadateľ") return require("@/images/avatar_coapplicant.png");
      return "";
    },
    debtAmount (debt) {
      if (debt.dti && debt.dti.value != null) return debt.dti.value;
      if (debt.zostatok && debt.zostatok.value != null) return debt.zostatok.value;
      if (debt.limit && debt.limit.value != null) return debt.limit.value;
      return 0;
    },
    debtPlan (debt) {
      return debt.mortgage_plan || "keep";
    },
    debtPlanClass (debt) {
      const plan = this.debtPlan(debt);
      return {
        excluded: plan === null,
        "plan-keep": plan === "keep",
        "plan-repay": plan === "repay",
        "plan-refinance": plan === "refinance",
      };
    },
    async changeDebtPlan (debt, plan) {
      if (this.updatingDebtId !== null || this.debtPlan(debt) === plan) return;
      this.updatingDebtId = debt.id;
      try {
        if (plan === "refinance") {
          await this.pickRefinanced(debt);
          return;
        }

        const clientObject = this.getClientType(debt.klient);
        await this.$store.dispatch("mortgageDetailState/setMortgageDebtPlan", {
          id: debt.id,
          clientLabel: clientObject.type,
          clientRole: clientObject.type_code,
          plan,
        });
      } finally {
        this.updatingDebtId = null;
      }
    },
    edit (debt) {
      if (this.type === "refinancovane") {
        this.$store.commit("popupManagerState/setClientInfo", debt.clientInfo);
      }
      this.debtTypePicker(debt.typ, debt);
    },
    getTotalAmount (amounts) {
      let totalAmount = 0;
      for (const amount of Object.values(amounts)) totalAmount += amount;
      return totalAmount;
    },
    checkIfHasBudget (leftover, amounts) {
      const limit = this.$store.getters["mortgageDetailState/getLoanLimit"];
      const amount = this.getTotalAmount(amounts);
      const amountLeft = limit - amount;
      return {
        amountOverflow: Math.round(leftover - amountLeft),
        isBudgetEnough: leftover <= amountLeft,
      };
    },
    showDialogToUser (label) {
      this.$store.commit("appState/setSuccessMessage", {text: label, type: "confirmation"});
    },
    waitForUserPermission () {
      return new Promise((resolve) => {
        const int = setInterval(() => {
          const userConfirmation = this.$store.getters["appState/getUserConfirmation"];
          if (userConfirmation === true) {
            this.$store.commit("appState/setUserConfirmation", null);
            clearInterval(int);
            resolve(true);
          } else if (userConfirmation === false) {
            this.$store.commit("appState/setUserConfirmation", null);
            clearInterval(int);
            resolve(false);
          }
        }, 100);
      });
    },
    lowerAmountsByOverflow (amountOverflow) {
      const amounts = this.$store.getters["mortgageDetailState/getAmounts"];
      if (amounts.suma_nadobudnutie >= amountOverflow) {
        const value = Math.round(Math.abs(amounts.suma_nadobudnutie - amountOverflow));
        return {amounts: {...amounts, suma_nadobudnutie: value}, label: `nadobudnutie na ${value}€`};
      }
      if ((amounts.suma_nadobudnutie + amounts.suma_bezucel) >= amountOverflow) {
        const value = Math.round(Math.abs(amounts.suma_bezucel + amounts.suma_nadobudnutie - amountOverflow));
        return {
          amounts: {...amounts, suma_nadobudnutie: 0, suma_bezucel: value},
          label: `nadobudnutie na 0€ a bezúčel na ${value}€`,
        };
      }
      if (this.getTotalAmount(amounts) >= amountOverflow) {
        const value = Math.round(Math.abs(
          amounts.suma_nadobudnutie + amounts.suma_bezucel + amounts.suma_refinancovanie - amountOverflow
        ));
        return {
          amounts: {suma_nadobudnutie: 0, suma_bezucel: 0, suma_refinancovanie: value},
          label: `nadobudnutie na 0€ bezúčel na 0€ refinancovanie na ${value}€`,
        };
      }
      return false;
    },
    constructMessageLabel (restOfLabel) {
      return "Pre refinancovanie dlhu bude potrebné nastaviť " + restOfLabel;
    },
    async pickRefinanced (debt) {
      if (this.activeMortgageId === null) {
        this.$store.commit("appState/setSuccessMessage", {
          text: "Refinancovanie sa dá nastaviť iba v detaile hypotéky",
          type: "error",
        });
        return false;
      }
      const amounts = this.$store.getters["mortgageDetailState/getAmounts"];
      const budgetData = this.checkIfHasBudget(debt.zostatok, amounts);
      if (!budgetData.isBudgetEnough) {
        const loweredAmountsObject = this.lowerAmountsByOverflow(budgetData.amountOverflow);
        if (!loweredAmountsObject) {
          this.$store.commit("appState/setSuccessMessage", {
            text: "Pre refinancovanie je potrebné zvýšiť príjem",
            type: "error",
          });
          return false;
        }
        this.showDialogToUser(this.constructMessageLabel(loweredAmountsObject.label));
        const userPermission = await this.waitForUserPermission();
        if (!userPermission) return false;
        this.$store.commit("mortgageDetailState/setAmounts", loweredAmountsObject.amounts);
      }
      const clientObject = this.getClientType(debt.klient);
      const debtObject = {
        id: debt.id,
        plan: "refinance",
        clientRole: clientObject.type_code,
        clientLabel: clientObject.type,
      };
      await this.$store.dispatch("mortgageDetailState/setMortgageDebtPlan", debtObject);
      return true;
    },
    async pick (debt) {
      if (this.type === "refinancovane") {
        await this.pickRefinanced(debt);
      } else if (this.type === "classic") {
        await this.$store.dispatch("mortgageDetailState/toggleClientDebt", {
          id: debt.id,
          clientLabel: this.clientInfo.type,
          clientRole: this.clientInfo.type_code,
          isActive: true,
        });
      } else if (this.type === "pickerOnly") {
        this.$root.$emit("debt-picked", {id: debt.id});
        this.closeDialog();
      }
    },
    async removeDebt (debt) {
      if (debt.can_delete === false || this.updatingDebtId !== null) return;
      this.showDialogToUser(
        `Naozaj chcete zmazať výdavok „${this.translateDebtType(debt.typ)}“?`,
      );
      const userPermission = await this.waitForUserPermission();
      if (!userPermission) return;

      const clientObject = this.getClientType(debt.klient);
      this.updatingDebtId = debt.id;
      try {
        await this.$store.dispatch("mortgageDetailState/deleteClientDebt", {
          id: debt.id,
          clientRole: clientObject.type_code,
        });
        this.$store.commit("appState/setSuccessMessage", {
          text: "Výdavok bol zmazaný",
          type: "success",
        });
      } catch (error) {
        this.$store.commit("appState/setSuccessMessage", {
          text: error?.response?.data?.detail || "Výdavok sa nepodarilo zmazať",
          type: "error",
        });
      } finally {
        this.updatingDebtId = null;
      }
    },
    closeDialog () {
      this.$store.commit("popupManagerState/popItemFromPopupOrder");
    },
    debtTypePicker (debtType, debtData = null) {
      this.$store.commit("popupManagerState/pushToPopupOrder", {
        type: {debtType, debtPurpose: this.type},
        component: "ClientDebtPopUp",
        payload: debtData,
        rules: this.rules,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.record-picker-popup,
.record-picker-content {
  overflow-x: hidden;
}

.record-picker-content,
.record-picker-content > * {
  box-sizing: border-box;
  min-width: 0;
  max-width: 100%;
}

.picker-section {
  display: grid;
  gap: 12px;
}

.section-intro {
  margin-bottom: 4px;
}

.section-intro-title {
  color: var(--app-text);
  font-size: 16px;
  font-weight: 900;
}

.section-intro-copy {
  margin-top: 3px;
  color: var(--app-text-muted);
  font-size: 12px;
  line-height: 1.45;
}

.record-card {
  display: grid;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--app-border);
  border-radius: 18px;
  background: var(--app-surface);
  box-shadow: var(--app-shadow);
  transition: border-color .18s ease, opacity .18s ease;
}

.record-card.excluded {
  border-style: dashed;
  background: color-mix(in srgb, var(--app-surface-muted) 82%, transparent);
}

.record-card.plan-keep {
  border-color: color-mix(in srgb, var(--app-primary) 34%, var(--app-border));
}

.record-card.plan-repay {
  border-color: color-mix(in srgb, var(--app-success) 36%, var(--app-border));
}

.record-card.plan-refinance {
  border-color: color-mix(in srgb, var(--app-primary) 52%, var(--app-border));
  background: color-mix(in srgb, var(--app-primary) 5%, var(--app-surface));
}

.record-main {
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  gap: 4px;
}

.record-name {
  min-width: 0;
  overflow-wrap: anywhere;
  color: var(--app-text);
  font-weight: 800;
  line-height: 1.35;
}

.record-type {
  color: var(--app-text-muted);
  font-weight: 600;
}

.record-amount {
  flex: 0 0 auto;
  color: var(--app-text);
  font-weight: 900;
  white-space: nowrap;
}

.record-warning {
  padding: 9px 11px;
  border-radius: 10px;
  background: color-mix(in srgb, #f5a524 14%, transparent);
  color: var(--app-text);
  font-size: 11px;
  line-height: 1.4;
}

.record-actions {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 8px;
  align-items: start;
}

.record-actions.single {
  grid-template-columns: 1fr;
}

.record-management {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, .7fr);
  gap: 8px;
}

.record-edit,
.record-delete,
.record-select,
.empty-records-action {
  display: inline-flex;
  min-height: 44px;
  padding: 0 14px;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 1px solid var(--app-border);
  border-radius: 12px;
  background: var(--app-surface-muted);
  color: var(--app-primary);
  font-weight: 800;
  cursor: pointer;
}

.record-edit {
  width: 100%;
  align-self: start;
}

.record-delete {
  color: #cf4f65;
}

.record-edit:disabled,
.record-delete:disabled {
  cursor: wait;
  opacity: .55;
}

.record-delete-locked {
  grid-column: 1 / -1;
  padding: 9px 11px;
  border-radius: 10px;
  background: color-mix(in srgb, var(--app-primary) 7%, var(--app-surface));
  color: var(--app-text-muted);
  font-size: 11px;
  line-height: 1.4;
}

.record-select {
  background: color-mix(in srgb, var(--app-primary) 10%, var(--app-surface));
}

.record-edit:hover,
.record-edit:focus-visible,
.record-delete:hover,
.record-delete:focus-visible,
.record-select:hover,
.record-select:focus-visible,
.empty-records-action:hover,
.empty-records-action:focus-visible {
  border-color: var(--app-primary);
  outline: none;
}

.empty-records {
  display: grid;
  padding: 30px 20px;
  justify-items: center;
  gap: 8px;
  border: 1px dashed var(--app-border);
  border-radius: 18px;
  background: var(--app-surface-muted);
  text-align: center;
}

.empty-records-icon {
  display: inline-flex;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  border-radius: 13px;
  background: var(--app-primary);
  color: #fff;
}

.empty-records-title {
  color: var(--app-text);
  font-weight: 900;
}

.empty-records-copy {
  max-width: 310px;
  color: var(--app-text-muted);
  font-size: 12px;
  line-height: 1.45;
}

.empty-records-action {
  margin-top: 8px;
  background: var(--app-primary);
  color: #fff;
}

.record-option {
  min-width: 0;
  margin-right: 0;
  border: 1px solid var(--app-border);
  color: var(--app-text);
  text-align: left;
}

.record-option:hover,
.record-option:focus-visible {
  border-color: var(--app-primary);
  outline: none;
  color: var(--app-primary);
}

.popup {
  width: 35%;
  position: absolute !important;
  top: 0;
  overflow-y: auto;
  transition-duration: .5s;
}

@media (max-width: 480px) {
  .record-actions {
    grid-template-columns: 1fr;
  }
}
</style>
